import { forwardRef, useEffect, useImperativeHandle } from 'react';
import {
  $createParagraphNode, $createTextNode, $getNodeByKey, $getRoot, $getSelection, $isRangeSelection,
  // $setSelection, BaseSelection,
  COMMAND_PRIORITY_CRITICAL, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW,
  COPY_COMMAND, CUT_COMMAND, PASTE_COMMAND, SELECTION_CHANGE_COMMAND,
  KEY_ARROW_DOWN_COMMAND, KEY_ARROW_RIGHT_COMMAND, KEY_ARROW_UP_COMMAND, KEY_BACKSPACE_COMMAND, KEY_DELETE_COMMAND, KEY_ENTER_COMMAND,
  MutationListener, ParagraphNode, RangeSelection, TextNode,
  KEY_ESCAPE_COMMAND,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getClipboardDataFromSelection, copyToClipboard } from '@lexical/clipboard';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';

import * as parser from './parser';
import { PrefixNode, PrefixOption } from './prefix-node';
import { ValueNode } from './value-node';
import { $getSelectedNode } from './utils';

const textNodeTransform = (prefixes: PrefixOption[]) => (node: TextNode) => {
  const text = node.getTextContent();
  const parsed = parser.parseText(prefixes, text);
  const lastNode = parsed.pop();
  let head = node;
  parsed.reverse().forEach((item) => {
    const newNode = parser.$create(text, item);
    head.insertBefore(newNode);
    head = newNode;
  });
  if (lastNode.type === TextNode) {
    if (parsed.length === 0) return;
    node.setTextContent(parser.extractText(text, lastNode));
  } else {
    node.replace(parser.$create(text, lastNode));
  }
};

const valueNodeTransform = (prefixes: PrefixOption[]) => (node: ValueNode) => {
  const text = node.getTextContent();
  const parsed = parser.parseValue(prefixes, text);
  const lastNode = parsed.pop();
  let head: TextNode | PrefixNode | ValueNode = node;
  parsed.reverse().forEach((item) => {
    const newNode = parser.$create(text, item);
    head.insertBefore(newNode);
    head = newNode;
  });
  if (lastNode.type === ValueNode) {
    node.setTextContent(parser.extractText(text, lastNode));
  } else {
    node.replace(parser.$create(text, lastNode));
  }
};

export interface SearchPluginSelection {
  type: parser.NodeType | typeof ParagraphNode;
  content: string;
  offset: number;
  siblingContent: string;
}

export interface SearchPluginOptions {
  prefixes: PrefixOption[];
  onUpdateSelection?: (selection: SearchPluginSelection | null) => void;
  onUpdateContent?: (content: string) => void;
  onPressEnter?: () => void;
  onPressDown?: () => void;
  onPressUp?: () => void;
  onPressEscape?: () => void;
}

export interface SearchPluginApi {
  setContent: (text: string) => void;
  setCurrentNodeText: (text: string) => void;
  focusNextNode: () => void;
  focusEnd: () => void;
  blur: () => void;
}

export const SearchPlugin = forwardRef<SearchPluginApi, SearchPluginOptions>(({
  prefixes, onUpdateSelection, onUpdateContent, onPressEnter, onPressDown, onPressUp, onPressEscape,
}, ref) => {
  const [editor] = useLexicalComposerContext();
  useImperativeHandle(ref, () => ({
    setContent: (text) => editor.update(() => {
      const processed = parser.processRaw(prefixes, text);
      const parsed = parser.parseText(prefixes, processed);
      const nodes = parsed.map((item) => parser.$create(processed, item));
      const root = $getRoot();
      root.clear();
      const paragraph = $createParagraphNode();
      paragraph.append(...nodes);
      root.append(paragraph);
    }),
    setCurrentNodeText: (text) => editor.update(() => {
      const node = $getSelectedNode();
      if (node instanceof ParagraphNode) {
        node.append($createTextNode(text));
      } else {
        node.setTextContent(text);
        node.selectEnd();
      }
    }),
    focusNextNode: () => editor.update(() => {
      const node = $getSelectedNode();
      const nextNode = node.getNextSibling();
      if (!nextNode) {
        const newNode = $createTextNode(' ');
        node.insertAfter(newNode);
        newNode.selectEnd();
      } else {
        nextNode.selectStart();
      }
    }),
    focusEnd: () => editor.update(() => {
      $getRoot().selectEnd();
    }),
    blur: () => editor.update(() => {
      editor.getRootElement().blur();
    }),
  }), [editor]);
  useEffect(() => {
    const transforms = [
      editor.registerNodeTransform(TextNode, textNodeTransform(prefixes)),
      editor.registerNodeTransform(ValueNode, valueNodeTransform(prefixes)),
    ];

    const prefixNodeMutation: MutationListener = (mutatedNodes) => {
      [...mutatedNodes.keys()].forEach((nodeKey) => {
        const mutation = mutatedNodes.get(nodeKey);
        if (mutation === 'created') {
          editor.update(() => {
            const node = $getNodeByKey(nodeKey);
            const value = node.getNextSibling();
            if (value) {
              const currentNode = $getSelectedNode();
              if ([node.getKey(), value.getKey()].includes(currentNode.getKey())) {
                value.selectEnd();
              }
            }
          });
        } else if (mutation === 'destroyed') {
          // editor.update(() => {
          //   const node = $getNodeByKey(nodeKey);
          //   console.log('mutated node: ', node);
          //   const value = node.getNextSibling();
          //   console.log('value?: ', value);
          // });
        }
      });
    };

    const mutations = [
      editor.registerMutationListener(PrefixNode, prefixNodeMutation, { skipInitialization: true }),
    ];

    const handleDelete = (evt: KeyboardEvent) => {
      const node = $getSelectedNode();
      if (node instanceof ValueNode) {
        if (node.isEmpty()) {
          evt.preventDefault();
          node.selectPrevious();
          return true;
        } else if (node.getTextContentSize() === 1) {
          evt.preventDefault();
          node.setTextContent('');
          return true;
        }
      } else if (node instanceof PrefixNode) {
        const nextNode = node.getNextSibling();
        if (nextNode instanceof ValueNode) nextNode.remove();
      }
      return false;

      // // If the custom node only has 1 character left…
      // if (myNode.getTextContentSize() === 1) {
      //   // Prevent the default browser/lexical behavior (which would remove the node)
      //   e?.preventDefault();

      //   // Replace that last char with a space so the node isn’t empty
      //   const text = myNode.getFirstChild();
      //   if ($isTextNode(text)) {
      //     text.setTextContent(' ');

      //     // Reset the cursor back to the start of the node,
      //     // so the user stays inside it instead of being ejected
      //     sel.anchor.set(text.getKey(), 0, 'text');
      //     sel.focus.set(text.getKey(), 0, 'text');
      //   }
      //   return true; // Tell Lexical: “I handled this event”
      // }
    };

    const handleEnter = (evt: KeyboardEvent) => {
      evt.preventDefault();
      try {
        onPressEnter?.();
      } catch (err) {
        console.error(err);
      }
      return true;
    };

    const handleEscape = (evt: KeyboardEvent) => {
      evt.preventDefault();
      try {
        onPressEscape?.();
      } catch (err) {
        console.error(err);
      }
      return true;
    };

    const handleArrowDown = (evt: KeyboardEvent) => {
      evt.preventDefault();
      try {
        onPressDown?.();
      } catch (err) {
        console.error(err);
      }
      return true;
    };

    const handleArrowUp = (evt: KeyboardEvent) => {
      evt.preventDefault();
      try {
        onPressUp?.();
      } catch (err) {
        console.error(err);
      }
      return true;
    };

    const handleRight = (evt: KeyboardEvent) => {
      const node = $getSelectedNode();
      if (node instanceof ValueNode) {
        const selection = $getSelection() as RangeSelection;
        if (selection.anchor.offset === node.getTextContentSize()) {
          if (!node.getNextSibling()) {
            evt.preventDefault();
            node.finalize();
            return true;
          }
        }
      }
      return false;
    };

    const handleCopy = (evt: ClipboardEvent) => {
      evt.preventDefault();
      const sel = $getSelection();
      const data = $getClipboardDataFromSelection(sel as any);
      if (data['text/plain'].length > 0) {
        delete data['text/html'];
        delete data['application/x-lexical-editor']; // delete lexical representation because pasting requires pre-processing
        data['text/plain'] = parser.serialize(sel as RangeSelection);
        copyToClipboard(editor, evt, data);
      }
      return true;
    };

    const handleCut = (evt: ClipboardEvent) => {
      evt.preventDefault();
      const sel = $getSelection();
      if (!$isRangeSelection(sel)) return true;
      
      const data = $getClipboardDataFromSelection(sel as any);
      if (data['text/plain'].length > 0) {
        delete data['text/html'];
        delete data['application/x-lexical-editor'];
        data['text/plain'] = parser.serialize(sel);
        copyToClipboard(editor, evt, data);
        sel.removeText();
      }
      return true;
    };

    const handlePaste = (evt: ClipboardEvent) => {
      evt.preventDefault();
      const clipboard = evt.clipboardData;
      if (!clipboard) return true;

      const plain = clipboard.getData('text/plain');
      // const selectedNode = $getSelectedNode();
      const processed = parser.processRaw(prefixes, plain);
      const parsed = parser.parseText(prefixes, processed);
      const nodes = parsed.map((item) => parser.$create(processed, item));
      const sel = $getSelection();
      sel.insertNodes(nodes);
      return true;
    };

    const getSelectionData = (selection: RangeSelection, node: InstanceType<parser.NodeType>) => {
      const content = node.getTextContent();
      const offset = selection.anchor.offset;
      let siblingContent = '';
      if (node instanceof PrefixNode) siblingContent = node.getNextSibling()?.getTextContent();
      else if (node instanceof ValueNode) siblingContent = node.getPreviousSibling().getTextContent();
      return { type: node.constructor, content, offset, siblingContent };
    };

    const handleChangeSelection = () => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) && selection.isCollapsed()) {
        const node = $getSelectedNode();
        // if (node instanceof PrefixNode) {
        //   const nodeSelection = $createNodeSelection();
        //   nodeSelection.add(node.getKey());
        //   $setSelection(nodeSelection);
        //   return true;
        // }
        onUpdateSelection?.(getSelectionData(selection, node));
      } else {
        onUpdateSelection?.(null);
      }

      const nodes = selection.getNodes();
      if (nodes.length === 1) {
        const selectedNode = nodes[0];
        const root = $getRoot();
        const allNodes = root.getAllTextNodes();
        allNodes.forEach((textNode) => {
          if (textNode instanceof ValueNode) {
            if (textNode.getKey() === selectedNode.getKey()) {
              textNode.setActive(true);
            } else {
              textNode.setActive(false);
            }
          }
        });
      }
      
      return false;
    };

    const commands = [
      editor.registerCommand(COPY_COMMAND, handleCopy, COMMAND_PRIORITY_CRITICAL),
      editor.registerCommand(CUT_COMMAND, handleCut, COMMAND_PRIORITY_CRITICAL),
      editor.registerCommand(PASTE_COMMAND, handlePaste, COMMAND_PRIORITY_CRITICAL),

      editor.registerCommand(KEY_BACKSPACE_COMMAND, handleDelete, COMMAND_PRIORITY_HIGH),
      editor.registerCommand(KEY_DELETE_COMMAND, handleDelete, COMMAND_PRIORITY_HIGH),
      editor.registerCommand(KEY_ENTER_COMMAND, handleEnter, COMMAND_PRIORITY_HIGH),
      editor.registerCommand(KEY_ESCAPE_COMMAND, handleEscape, COMMAND_PRIORITY_HIGH),

      editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, handleRight, COMMAND_PRIORITY_CRITICAL),
      onPressDown && editor.registerCommand(KEY_ARROW_DOWN_COMMAND, handleArrowDown, COMMAND_PRIORITY_HIGH),
      onPressUp && editor.registerCommand(KEY_ARROW_UP_COMMAND, handleArrowUp, COMMAND_PRIORITY_HIGH),

      editor.registerCommand(SELECTION_CHANGE_COMMAND, handleChangeSelection, COMMAND_PRIORITY_LOW),
    ];

    return () => [transforms, mutations, commands].flat().forEach(deregister => deregister?.());
  }, [editor]);

  const handleChange = () => editor.read(() => {
    const node = $getRoot().getFirstDescendant();
    const serialized = parser.serialize(node as TextNode);
    onUpdateContent?.(serialized);
  });

  return onUpdateContent ? <OnChangePlugin onChange={handleChange} /> : null;
});
